import { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AnnouncementForm from './AnnouncementForm';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { RecipientGroup, RSVP, Unit, EventStatus, TicketType } from '@/types';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import { isMultiTenantEvent } from '@/lib/ticket-mode';
import {
  sendAnnouncementInBatches,
  type SendAnnouncementEmailPayload,
  type SendAnnouncementEmailResult,
  type SendAnnouncementProgress,
} from '@/lib/send-announcement-batches';
import { Megaphone, Users, Calendar } from 'lucide-react';

interface PublishEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  unitId: string;
  eventName: string;
  eventStatus: EventStatus;
  onPublished: (status: 'interest-only' | 'published') => void;
}

export default function PublishEventModal({
  open,
  onOpenChange,
  eventId,
  unitId,
  eventName,
  eventStatus,
  onPublished,
}: PublishEventModalProps) {
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [sendProgress, setSendProgress] = useState<SendAnnouncementProgress | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [subscribedGuestsCount, setSubscribedGuestsCount] = useState(0);
  const [unitMembersCount, setUnitMembersCount] = useState(0);
  const [sendAnnouncement, setSendAnnouncement] = useState(false);
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [ticketMode, setTicketMode] = useState<'mealChoice' | 'multiTenant'>('mealChoice');

  // Determine if event is interest-only
  const isInterestOnly = eventStatus === 'interest-only';

  // Tickets may have no courses by design (e.g. bus tickets, single-entity tickets) — just require at least one ticket.
  const hasValidTickets = tickets.length > 0;

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, eventId, unitId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Event data to get tickets
      const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        setTickets(eventData.tickets || []);
        setTicketMode(isMultiTenantEvent(eventData) ? 'multiTenant' : 'mealChoice');
      }

      // Fetch Unit data
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (unitDoc.exists()) {
        const unitData = unitDoc.data();
        const fetchedUnit: Unit = {
          id: unitDoc.id,
          name: unitData.name,
          unitNumber: unitData.unitNumber,
          typeId: unitData.typeId || '',
          members: unitData.members || [],
          createdAt: unitData.createdAt?.toDate() || new Date(),
          subscription: unitData.subscription,
          settings: unitData.settings || {},
          guestList: unitData.guestList || [],
        };
        setUnit(fetchedUnit);
        setSubscribedGuestsCount(unitData.guestList?.length || 0);
        setUnitMembersCount(unitData.members?.length || 0);
      }

      // Fetch RSVPs
      const rsvpsSnapshot = await getDocs(
        collection(db, 'units', unitId, 'events', eventId, 'rsvps')
      );
      const rsvpData: RSVP[] = [];
      rsvpsSnapshot.forEach((doc) => {
        const data = doc.data();
        rsvpData.push({
          id: doc.id,
          eventId,
          userId: data.userId,
          name: data.name,
          rank: data.rank,
          unit: data.unit,
          email: data.email,
          responses: data.responses,
          status: data.status,
          invoiceId: data.invoiceId,
          paid: data.paid,
          invitedBy: data.invitedBy,
          checkedIn: data.checkedIn,
          checkInTime: data.checkInTime?.toDate(),
          specialRequests: data.specialRequests,
          plusOneAllowed: data.plusOneAllowed,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        });
      });
      setRsvps(rsvpData);
    } catch (error) {
      console.error('Error fetching data:', error);
      showError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const recipientOptions: MultiSelectOption[] = [
    { value: 'all', label: 'All Attendees', count: rsvps.length },
    { value: 'confirmed', label: 'Confirmed Only', count: rsvps.filter((r) => r.status === 'confirmed').length },
    { value: 'waitlist', label: 'Registered Interest', count: rsvps.filter((r) => r.status === 'waitlist').length },
    { value: 'subscribers', label: 'Subscribed Guests', count: subscribedGuestsCount },
    { value: 'unit-members', label: 'Unit Members', count: unitMembersCount },
  ];

  const handlePublish = async (publishType: 'interest-only' | 'published', message?: string, sendTo?: RecipientGroup[], includeRsvpLink?: boolean) => {
    // Validate that at least one ticket exists for published events
    if (publishType === 'published' && !hasValidTickets) {
      showError('Please define at least one ticket before publishing the event.');
      return;
    }

    const confirmText = publishType === 'interest-only'
      ? 'This will publish the event for interest registration. People can register their interest but won\'t be able to RSVP with full details yet.'
      : 'This will make the event fully visible and allow guests to RSVP with all details.';

    const result = await confirmAction({
      title: publishType === 'interest-only' ? 'Publish for Interest Registration?' : 'Publish Event?',
      text: confirmText,
    });

    if (!result.isConfirmed) return;

    setPublishing(true);
    try {
      // Update event status
      await updateDoc(doc(db, 'units', unitId, 'events', eventId), {
        eventStatus: publishType,
        publishedAt: serverTimestamp(),
      });

      if (message && sendTo && sendTo.length > 0) {
        setSendProgress({ sent: 0, total: 0 });

        const announcementRef = await addDoc(
          collection(db, 'units', unitId, 'events', eventId, 'announcements'),
          {
            message: message.trim(),
            createdBy: 'system',
            createdAt: serverTimestamp(),
            sentTo: sendTo,
            readBy: [],
            includeRsvpLink: includeRsvpLink || false,
          }
        );

        const sendAnnouncementEmail = httpsCallable<
          SendAnnouncementEmailPayload & { batchOffset: number; batchSize: number },
          SendAnnouncementEmailResult
        >(functions, 'sendAnnouncementEmail');
        const emailResult = await sendAnnouncementInBatches(
          (payload) => sendAnnouncementEmail(payload),
          {
            unitId,
            eventId,
            announcementId: announcementRef.id,
            message: message.trim(),
            sendTo,
            includeRsvpLink: includeRsvpLink || false,
          },
          setSendProgress
        );

        if (emailResult.totalRecipients > 0) {
          await updateDoc(announcementRef, {
            recipientCount: emailResult.totalRecipients,
          });
        }

        showSuccess(
          `Event published ${publishType === 'interest-only' ? 'for interest registration' : 'successfully'}! Announcement sent to ${emailResult.totalRecipients} recipient(s).`
        );
      } else {
        showSuccess(`Event published ${publishType === 'interest-only' ? 'for interest registration' : 'successfully'}!`);
      }

      onPublished(publishType);
      onOpenChange(false);
    } catch (error) {
      console.error('Error publishing event:', error);
      showError('Failed to publish event');
    } finally {
      setPublishing(false);
      setSendProgress(null);
    }
  };

  const handleSendAnnouncement = async (message: string, sendTo: RecipientGroup[], includeRsvpLink: boolean, publishType: 'interest-only' | 'published') => {
    await handlePublish(publishType, message, sendTo, includeRsvpLink);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Publish Event</DialogTitle>
          <DialogDescription>
            Choose how to publish "{eventName}"
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </div>
        ) : (
          <Tabs defaultValue={isInterestOnly ? "full" : "interest"} className="w-full">
            {!isInterestOnly && (
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="interest">
                  <Users className="mr-2 h-4 w-4" />
                  Interest Registration
                </TabsTrigger>
                <TabsTrigger value="full">
                  <Calendar className="mr-2 h-4 w-4" />
                  Full Event
                </TabsTrigger>
              </TabsList>
            )}
            {isInterestOnly && (
              <TabsList className="grid w-full grid-cols-1">
                <TabsTrigger value="full">
                  <Calendar className="mr-2 h-4 w-4" />
                  Publish Full Event
                </TabsTrigger>
              </TabsList>
            )}

            {!isInterestOnly && (
              <TabsContent value="interest" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Publish for Interest Registration
                    </CardTitle>
                    <CardDescription>
                      Allow people to register their interest before you finalize all event details.
                      Perfect for gathering early commitments while you arrange catering, venue, etc.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">What happens:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
                        <li>Event becomes visible to members</li>
                        <li>People can register their interest</li>
                        <li>No full RSVP details required yet</li>
                        <li>You can upgrade to full event later</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Send announcement?</label>
                        <Button
                          variant={sendAnnouncement ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSendAnnouncement(!sendAnnouncement)}
                        >
                          {sendAnnouncement ? 'Yes' : 'No'}
                        </Button>
                      </div>

                      {sendAnnouncement ? (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">Announcement Details</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <AnnouncementForm
                              recipientOptions={recipientOptions}
                              onSend={(message, sendTo, includeRsvpLink) =>
                                handleSendAnnouncement(message, sendTo, includeRsvpLink, 'interest-only')
                              }
                              sending={publishing}
                              sendProgress={sendProgress}
                              defaultSendTo={['unit-members']}
                              defaultIncludeRsvpLink={true}
                              submitButtonText="Publish & Send"
                            />
                          </CardContent>
                        </Card>
                      ) : (
                        <Button
                          className="w-full"
                          onClick={() => handlePublish('interest-only')}
                          disabled={publishing}
                        >
                          Publish for Interest Registration
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            <TabsContent value="full" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Publish Full Event
                  </CardTitle>
                  <CardDescription>
                    Publish the complete event with all details. Members can RSVP with full information.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!hasValidTickets && (
                    <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-200">
                      <h4 className="font-semibold text-yellow-900 mb-2">Missing Information:</h4>
                      <p className="text-sm text-yellow-800">
                        You must define at least one ticket before publishing the event. Please go to Event Settings to add tickets.
                      </p>
                    </div>
                  )}

                  <div className="rounded-lg bg-green-50 p-4 border border-green-200">
                    <h4 className="font-semibold text-green-900 mb-2">What happens:</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                      <li>Event becomes fully visible</li>
                      <li>Full RSVP form with all details</li>
                      <li>Payment collection enabled</li>
                      {ticketMode === 'multiTenant' ? (
                        <li>Guests choose ticket quantities (no meal choices)</li>
                      ) : (
                        <li>Meal choices and dietary requirements</li>
                      )}
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Send announcement?</label>
                      <Button
                        variant={sendAnnouncement ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSendAnnouncement(!sendAnnouncement)}
                        disabled={!hasValidTickets}
                      >
                        {sendAnnouncement ? 'Yes' : 'No'}
                      </Button>
                    </div>

                    {sendAnnouncement ? (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Announcement Details</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <AnnouncementForm
                            recipientOptions={recipientOptions}
                            onSend={(message, sendTo, includeRsvpLink) =>
                              handleSendAnnouncement(message, sendTo, includeRsvpLink, 'published')
                            }
                            sending={publishing}
                            sendProgress={sendProgress}
                            defaultSendTo={['unit-members']}
                            defaultIncludeRsvpLink={true}
                            submitButtonText="Publish & Send"
                          />
                        </CardContent>
                      </Card>
                    ) : (
                      <Button
                        className="w-full"
                        onClick={() => handlePublish('published')}
                        disabled={publishing || !hasValidTickets}
                      >
                        Publish Full Event
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

